import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Brand } from "@/components/Brand";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Sparkles, Loader2 } from "lucide-react";

const SERVICES = ["Website Development", "Website Redesign", "SEO", "Local SEO", "Google Business Profile Optimization", "Social Media Marketing", "Paid Advertising", "Branding", "Content Marketing", "WhatsApp Automation", "AI Chatbots", "CRM Automation", "Lead Automation", "Appointment Automation", "Custom Software", "AI Solutions"];
const INDUSTRIES = ["Salon", "Restaurant", "Gym", "Dentist", "Jewellery", "Real Estate", "Hotel", "Cafe", "Bakery", "Boutique", "Clinic", "Coaching Institute", "Interior Designer", "Automobile", "Local Services"];
const TONES = ["Professional", "Friendly", "Direct", "Consultative", "Premium", "Minimal"];
const DEALS = [{ l: "₹10K–₹25K", min: 10000, max: 25000 }, { l: "₹25K–₹50K", min: 25000, max: 50000 }, { l: "₹50K–₹1L", min: 50000, max: 100000 }, { l: "₹1L+", min: 100000, max: 300000 }];

function Chip({ active, children, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className={`px-3.5 py-2 rounded-lg text-sm border transition-all ${active ? "bg-primary/15 border-primary/50 text-primary" : "bg-slate-900 border-slate-700 text-slate-300 hover:border-slate-600"}`}>
      {children}{active && <Check className="w-3.5 h-3.5 inline ml-1.5" />}
    </button>
  );
}

export default function Onboarding() {
  const { setProfile } = useAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    services: [], let_ai_decide: true, country: "India", city: "Pune", pincode: "", radius_km: 15,
    industries: [], deal_min: 25000, deal_max: 90000, agency_name: "", website: "", description: "",
    email: "", whatsapp: "", tone: "Consultative", currency: "INR",
  });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggle = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));

  const steps = ["What you sell", "Where", "Who", "Deal value", "Your agency", "Tone"];

  const finish = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/profile", { ...f, onboarded: true });
      setProfile(data);
      toast.success("Agency profile saved");
      navigate("/app");
    } catch (e) {
      toast.error("Could not save. Try again.");
    } finally {
      setSaving(false);
    }
  };

  const next = () => (step < 5 ? setStep(step + 1) : finish());

  return (
    <div className="min-h-screen bg-background lf-radial">
      <div className="max-w-2xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex items-center justify-between mb-8">
          <Brand />
          <button className="text-xs text-slate-500 hover:text-slate-300" data-testid="onboarding-skip" onClick={finish}>Skip — use demo defaults</button>
        </div>

        {/* Progress */}
        <div className="flex items-center gap-1.5 mb-8">
          {steps.map((s, i) => (
            <div key={s} className="flex-1">
              <div className={`h-1 rounded-full transition-all ${i <= step ? "bg-primary" : "bg-slate-800"}`} />
            </div>
          ))}
        </div>

        <div className="lf-card p-6 sm:p-8 lf-rise" key={step}>
          <p className="text-xs font-mono uppercase tracking-widest text-primary mb-1">Step {step + 1} of 6</p>

          {step === 0 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-50 mb-1">What do you sell?</h2>
              <p className="text-sm text-slate-400 mb-5">Pick the services you offer. Selling several? Let AI decide the best fit per prospect.</p>
              <button type="button" data-testid="let-ai-decide" onClick={() => set("let_ai_decide", !f.let_ai_decide)}
                className={`w-full flex items-center gap-3 p-4 rounded-xl border mb-4 transition-all ${f.let_ai_decide ? "bg-primary/10 border-primary/50" : "bg-slate-900 border-slate-700"}`}>
                <div className={`w-9 h-9 rounded-lg grid place-items-center ${f.let_ai_decide ? "bg-primary/20" : "bg-slate-800"}`}><Sparkles className="w-4 h-4 text-primary" /></div>
                <div className="text-left flex-1"><p className="text-sm font-semibold text-slate-100">Let AI decide <span className="text-[10px] font-mono text-primary ml-1">RECOMMENDED</span></p><p className="text-xs text-slate-400">Best for agencies selling multiple services.</p></div>
                {f.let_ai_decide && <Check className="w-5 h-5 text-primary" />}
              </button>
              <div className="flex flex-wrap gap-2">
                {SERVICES.map((s) => <Chip key={s} active={f.services.includes(s)} onClick={() => toggle("services", s)} testid={`service-${s}`}>{s}</Chip>)}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-50 mb-1">Where do you want clients?</h2>
              <p className="text-sm text-slate-400 mb-5">India is a first-class experience. More regions coming.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><Label className="text-slate-300 text-xs">Country</Label><Input value={f.country} onChange={(e) => set("country", e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-country" /></div>
                <div><Label className="text-slate-300 text-xs">City</Label><Input value={f.city} onChange={(e) => set("city", e.target.value)} placeholder="Pune" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-city" /></div>
                <div><Label className="text-slate-300 text-xs">PIN code</Label><Input value={f.pincode} onChange={(e) => set("pincode", e.target.value)} placeholder="411001" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-pin" /></div>
                <div><Label className="text-slate-300 text-xs">Radius (km)</Label><Input type="number" value={f.radius_km} onChange={(e) => set("radius_km", +e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-radius" /></div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-50 mb-1">Who do you want as clients?</h2>
              <p className="text-sm text-slate-400 mb-5">Choose target industries.</p>
              <div className="flex flex-wrap gap-2">
                {INDUSTRIES.map((s) => <Chip key={s} active={f.industries.includes(s)} onClick={() => toggle("industries", s)} testid={`industry-${s}`}>{s}</Chip>)}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-50 mb-1">What is a typical deal worth?</h2>
              <p className="text-sm text-slate-400 mb-5">Used to estimate opportunity value.</p>
              <div className="grid grid-cols-2 gap-3 mb-5">
                {DEALS.map((d) => <Chip key={d.l} active={f.deal_min === d.min && f.deal_max === d.max} onClick={() => { set("deal_min", d.min); set("deal_max", d.max); }} testid={`deal-${d.l}`}>{d.l}</Chip>)}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label className="text-slate-300 text-xs">Minimum (₹)</Label><Input type="number" value={f.deal_min} onChange={(e) => set("deal_min", +e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-deal-min" /></div>
                <div><Label className="text-slate-300 text-xs">Maximum (₹)</Label><Input type="number" value={f.deal_max} onChange={(e) => set("deal_max", +e.target.value)} className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-deal-max" /></div>
              </div>
            </div>
          )}

          {step === 4 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-50 mb-1">Tell us about your agency.</h2>
              <p className="text-sm text-slate-400 mb-5">This personalises every outreach message.</p>
              <div className="space-y-4">
                <div><Label className="text-slate-300 text-xs">Agency name</Label><Input value={f.agency_name} onChange={(e) => set("agency_name", e.target.value)} placeholder="Acme Digital" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-agency" /></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div><Label className="text-slate-300 text-xs">Website</Label><Input value={f.website} onChange={(e) => set("website", e.target.value)} placeholder="https://" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-website" /></div>
                  <div><Label className="text-slate-300 text-xs">WhatsApp</Label><Input value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} placeholder="+91" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-whatsapp" /></div>
                </div>
                <div><Label className="text-slate-300 text-xs">Contact email</Label><Input value={f.email} onChange={(e) => set("email", e.target.value)} placeholder="hello@agency.com" className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-email" /></div>
                <div><Label className="text-slate-300 text-xs">Short description</Label><Textarea value={f.description} onChange={(e) => set("description", e.target.value)} placeholder="What you do, who you help." className="mt-1.5 bg-slate-900 border-slate-700" data-testid="onb-desc" /></div>
              </div>
            </div>
          )}

          {step === 5 && (
            <div>
              <h2 className="font-display text-2xl font-bold text-slate-50 mb-1">How should your outreach sound?</h2>
              <p className="text-sm text-slate-400 mb-5">Sets the voice of every generated message.</p>
              <div className="flex flex-wrap gap-2">
                {TONES.map((t) => <Chip key={t} active={f.tone === t} onClick={() => set("tone", t)} testid={`tone-${t}`}>{t}</Chip>)}
              </div>
            </div>
          )}

          <div className="flex items-center justify-between mt-8 pt-6 border-t border-slate-800">
            <Button variant="ghost" disabled={step === 0} onClick={() => setStep(step - 1)} className="text-slate-400 hover:text-white" data-testid="onb-back">
              <ArrowLeft className="w-4 h-4 mr-1" /> Back
            </Button>
            <Button onClick={next} disabled={saving} className="bg-primary hover:bg-emerald-600 text-white" data-testid="onb-next">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : step === 5 ? (<>Find my best clients <ArrowRight className="w-4 h-4 ml-1.5" /></>) : (<>Continue <ArrowRight className="w-4 h-4 ml-1.5" /></>)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
